import { useRef, useEffect, ReactNode, useCallback, useState } from 'react';
// @mui
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Box,
  Typography,
  TextField,
  Stack,
} from '@mui/material';
import axiosAPI from '../../utils/axios';
import { PATH_DASHBOARD } from '../../routes/paths';

type Props = {
  show: boolean;
  close: () => void;
};

type AmountT = string | number;

// ----------------------------------------------------------------------

export default function AddFundsPopup({ show, close }: Props) {
  const descriptionElementRef = useRef<HTMLElement>(null);
  const [amount, setAmount] = useState<AmountT>('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (show) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement) {
        descriptionElement.focus();
      }
    }
  }, [show]);

  const createPayment = useCallback(async () => {
    setError('');
    if (!amount) {
      return;
    }
    if (amount < 100) {
      setError('Minimum deposit is 100$');
      return;
    }
    try {
      const response = await axiosAPI.post('payment/capitalist', {
        amount: +amount,
      });

      if (response.data.status) {
        window.open(response.data.result.paymentUrl, '_blank');
        setError('');
        close();
      } else {
        console.error(response);
        setError(response.data.result || 'Something went wrong');
      }
    } catch (err) {
      console.error(err);
    }
  }, [amount, close]);

  return (
    <div>
      <Dialog maxWidth="xl" open={show} onClose={close} scroll="body">
        <DialogTitle sx={{ pb: 0.5, textAlign: 'center', textTransform: 'uppercase' }}>
          Add Funds
        </DialogTitle>
        <Typography pb="12px" textAlign="center" fontSize="11px" color="text.secondary">
          With Capitalist
        </Typography>

        <DialogContent>
          <DialogContentText
            id="scroll-dialog-description"
            ref={descriptionElementRef}
            tabIndex={-1}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
              }}
            >
              <TextField
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                size="small"
                placeholder="Amount"
              />
              <Button variant="contained" color="info" onClick={createPayment}>
                Create Payment
              </Button>
            </Stack>
            {error && (
              <Typography
                variant="body2"
                textTransform="uppercase"
                fontSize={11}
                fontWeight={500}
                color="error.main"
              >
                {error}
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={close}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
